import Vue from 'vue'
import VueRouter from 'vue-router'
import { getToken } from '@/utils/auth'
import { getPartnerToken } from '@/portal/auth'
import store from '@/store'

Vue.use(VueRouter)

// 路由路径前缀 → 所需权限码
const PERMISSION_MAP = {
  '/dashboard': 'dashboard:view',
  '/settlement': 'merchant_apply:list',
  '/withdrawal': 'withdrawal:list',
  '/invoice': 'invoice:list',
  '/merchant': 'merchant:list',
  '/partner': 'partner:list',
  '/scenic': 'scenic:list',
  '/user': 'user:list',
  '/order': 'order:list',
  '/boat/voice': 'device:cmd',
  '/boat': 'boat:list',
  '/coupon': 'coupon:list',
  '/notification': 'notification:list',
  '/permission': 'role:list',
  '/report': 'report:view',
  '/system/config': 'system_config:list'
}

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { public: true }
  },
  // 合伙人门户（独立于管理端 Layout，顶层路由）
  {
    path: '/portal/login',
    name: 'PortalLogin',
    component: () => import('@/portal/PortalLogin.vue'),
    meta: { portal: true }
  },
  {
    path: '/portal',
    component: () => import('@/portal/PortalLayout.vue'),
    meta: { portal: true },
    redirect: '/portal/income',
    children: [
      {
        path: 'income',
        name: 'PortalIncome',
        component: () => import('@/portal/PortalIncome.vue')
      },
      {
        path: 'withdraw',
        name: 'PortalWithdraw',
        component: () => import('@/portal/PortalWithdraw.vue')
      }
    ]
  },
  {
    path: '/',
    component: () => import('@/views/Layout.vue'),
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: { title: '数据概览' }
      },
      {
        path: 'settlement',
        name: 'Settlement',
        component: () => import('@/views/Settlement.vue'),
        meta: { title: '入驻管理' }
      },
      {
        path: 'scenic',
        name: 'ScenicManagement',
        component: () => import('@/views/ScenicManagement.vue'),
        meta: { title: '景区管理' }
      },
      {
        path: 'scenic/:id',
        name: 'ScenicDetail',
        component: () => import('@/views/ScenicDetail.vue'),
        meta: { title: '景区详情' }
      },
      {
        path: 'withdrawal',
        name: 'Withdrawal',
        component: () => import('@/views/Withdrawal.vue'),
        meta: { title: '提现管理' }
      },
      {
        path: 'invoice',
        name: 'Invoice',
        component: () => import('@/views/Invoice.vue'),
        meta: { title: '发票管理' }
      },
      {
        path: 'merchant',
        name: 'Merchant',
        component: () => import('@/views/Merchant.vue'),
        meta: { title: '商家管理' }
      },
      {
        path: 'user',
        name: 'User',
        component: () => import('@/views/User.vue'),
        meta: { title: '用户管理' }
      },
      {
        path: 'order',
        name: 'Order',
        component: () => import('@/views/Order.vue'),
        meta: { title: '订单管理' }
      },
      {
        path: 'order/:id',
        name: 'OrderDetail',
        component: () => import('@/views/OrderDetail.vue'),
        meta: { title: '订单详情' }
      },
      {
        path: 'reconcile',
        name: 'Reconcile',
        component: () => import('@/views/Reconcile.vue'),
        meta: { title: '对账管理' }
      },
      {
        path: 'report/dispatch',
        name: 'DispatchReport',
        component: () => import('@/views/DispatchReport.vue'),
        meta: { title: '调度统计' }
      },
      {
        path: 'merchant/:id',
        name: 'MerchantDetail',
        component: () => import('@/views/MerchantDetail.vue'),
        meta: { title: '商家详情' }
      },
      {
        path: 'partner',
        name: 'Partner',
        component: () => import('@/views/Partner.vue'),
        meta: { title: '合伙人管理' }
      },
      {
        path: 'partner/:id',
        name: 'PartnerDetail',
        component: () => import('@/views/PartnerDetail.vue'),
        meta: { title: '合伙人详情' }
      },
      {
        path: 'boat/type',
        name: 'BoatType',
        component: () => import('@/views/BoatType.vue'),
        meta: { title: '船只类型' }
      },
      {
        path: 'boat/list',
        name: 'BoatList',
        component: () => import('@/views/BoatList.vue'),
        meta: { title: '船只列表' }
      },
      {
        path: 'boat/voice',
        name: 'VoiceManagement',
        component: () => import('@/views/VoiceManagement.vue'),
        meta: { title: '语音管理' }
      },
      {
        path: 'coupon',
        name: 'Coupon',
        component: () => import('@/views/Coupon.vue'),
        meta: { title: '优惠券管理' }
      },
      {
        path: 'notification',
        name: 'Notification',
        component: () => import('@/views/Notification.vue'),
        meta: { title: '消息管理' }
      },
      {
        path: 'permission',
        name: 'Permission',
        component: () => import('@/views/Permission.vue'),
        meta: { title: '系统权限' }
      },
      {
        path: 'system/config',
        name: 'SystemConfig',
        component: () => import('@/views/SystemConfig.vue'),
        meta: { title: '系统配置' }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach((to, from, next) => {
  // 合伙人门户独立鉴权（与管理端 token 互不干扰）
  if (to.path.startsWith('/portal')) {
    const t = getPartnerToken()
    if (to.path === '/portal/login') return t ? next('/portal/income') : next()
    if (!t) return next('/portal/login')
    return next()
  }
  const token = getToken()
  if (to.meta.public) {
    if (token && to.path === '/login') {
      next('/dashboard')
    } else {
      next()
    }
  } else {
    if (!token) {
      next('/login')
    } else {
      // 权限检查：查找匹配的权限要求
      const requiredPerm = Object.entries(PERMISSION_MAP).find(([prefix]) =>
        to.path.startsWith(prefix)
      )
      if (requiredPerm) {
        const perms = store.state.permissions || []
        if (perms.length > 0 && !perms.includes(requiredPerm[1])) {
          // 有权限列表但不包含所需权限，跳回首页
          next('/dashboard')
          return
        }
      }
      next()
    }
  }
})

export default router
